import type { Ref } from "vue";
import { useCollectiblesFilter } from "./useCollectiblesFilter";
import { normalizeFilterSlugsForApi, useFilterSearch, type MappedFilterValues } from "@/composables/filter";
import { FilterType, IDENTIFICATION_FILTER_SLUG, THEME_FILTER_SLUG, type FilterDefinition, type SortDefinition } from "@/services/filter";
import { ParameterSlug_Category } from "@/services/filter/filterHelpers";
import { fetchCategoryByUrl } from "@/services/collection/categories/categoriesApi";
import { useGettext } from "vue3-gettext";
import { CategoryId } from "@/services/collection/categories/categoriesSchema";
import type { CollectibleElastic } from "@/services/collectibles/elastic/collectiblesElasticTypes";
import { searchCollectiblesElastic } from "@/services/collectibles/elastic/collectiblesElasticApi";

const DEFAULT_PER_PAGE = 30;

type CollectiblesSearchRequest = {
  categoryId: number;
  page: number;
  perPage: number;
  filters: MappedFilterValues;
  identification?: string;
};

/**
 * Collectibles-specific search composable.
 * Uses useCollectiblesFilter() + useFilterSearch() with domain-specific API calls.
 */
export function useCollectiblesSearch(perPage: number = DEFAULT_PER_PAGE, shouldAppend?: Ref<boolean>) {
  const { $gettext } = useGettext();

  // Static identification filter definition (always first)
  const identificationFilter: FilterDefinition = {
    id: 0, // Special ID for identification filter
    slug: IDENTIFICATION_FILTER_SLUG,
    name: $gettext("Název"),
    type: FilterType.Text,
    values: [],
    intMin: 0,
    intMax: 0,
    unit: "",
  };

  const apiFilterDefinitions = ref<FilterDefinition[]>([]);
  const sortDefinition = ref<SortDefinition | null>(null);

  // Combine identification filter with API filter definitions
  const filterDefinitions = computed<FilterDefinition[]>(() => [identificationFilter, ...apiFilterDefinitions.value]);

  const { categoryUrl, filterValues, page, setFilter, setRangeFilter, setPage, resetFilters } = useCollectiblesFilter({ filterDefinitions });

  // Computed trigger that combines all reactive dependencies
  const searchTrigger = computed(() => ({
    categoryUrl: categoryUrl.value,
    filterValues: filterValues.value,
    page: page.value,
  }));

  const { items, totalItems, isLoading, error, refetch } = useFilterSearch<CollectibleElastic, CollectiblesSearchRequest>(searchTrigger, {
    perPage,
    shouldAppend,
    buildRequest: async (filters, currentPage, itemsPerPage) => {
      const category = await fetchCategoryByUrl(categoryUrl.value);
      if (category === undefined) {
        throw new Error("Category not found");
      }

      // Extract identification value before mapping (it's not in API definitions)
      const identification = typeof filters[IDENTIFICATION_FILTER_SLUG] === "string" ? filters[IDENTIFICATION_FILTER_SLUG] : undefined;

      // Remove identification from filters before mapping to IDs
      const filtersWithoutIdentification = { ...filters };
      delete filtersWithoutIdentification[IDENTIFICATION_FILTER_SLUG];

      const filtersRemapped = normalizeFilterSlugsForApi(filtersWithoutIdentification);

      return {
        categoryId: category.id,
        page: currentPage,
        perPage: itemsPerPage,
        filters: filtersRemapped,
        identification: identification || undefined,
      };
    },
    executeSearch: async (request, options) => {
      const response = await searchCollectiblesElastic(request, options);

      // Category must not be rendered as a database filter (it's handled separately via URL/category routing).
      // Theme (Edice) filter is hidden for Sports Cards.
      apiFilterDefinitions.value = response.parameters.filter((p) => p.slug !== ParameterSlug_Category && (p.slug !== THEME_FILTER_SLUG || request.categoryId !== CategoryId.SportsCards));
      // sortDefinition.value = response.sort; @todo Not implemented in API yet

      // On the first page with no active filters, hide items without images
      const isFirstPageNoFilter = request.page === 0 && Object.keys(request.filters).length === 0 && !request.identification;
      return {
        items: isFirstPageNoFilter && request.categoryId !== CategoryId.SportsCards ? response.items.filter((item) => item.images.length > 0) : response.items,
        totalItems: response.totalItems,
      };
    },
  });

  // Compute active filter count based on known filter definitions
  const activeFilterCount = computed(() => {
    let count = 0;

    // Count identification filter if set
    const identificationValue = filterValues.value[IDENTIFICATION_FILTER_SLUG];
    if (identificationValue && identificationValue !== "") {
      count++;
    }

    // Count API filters
    if (apiFilterDefinitions.value.length > 0) {
      const filterFields = new Set(apiFilterDefinitions.value.map((f) => f.slug));

      for (const [key, value] of Object.entries(filterValues.value)) {
        // Skip sort field and identification (already counted)
        if (sortDefinition.value && key === sortDefinition.value.field) continue;
        if (key === IDENTIFICATION_FILTER_SLUG) continue;

        // Check if this is a known filter field (handle range _min/_max)
        const baseField = key.replace(/_min$|_max$/, "");
        if (filterFields.has(baseField) && value !== undefined) {
          if (Array.isArray(value) ? value.length > 0 : value !== "") {
            count++;
          }
        }
      }
    }

    return count;
  });

  return {
    categoryUrl,
    filterValues,
    page,
    setFilter,
    setRangeFilter,
    setPage,
    resetFilters,
    items,
    totalItems,
    filterDefinitions,
    sortDefinition,
    isLoading,
    error,
    activeFilterCount,
    refetch,
  };
}
